<template>
  <div class="totp-step" data-test="totp-step">
    <h2 class="totp-step__title">{{ t('auth.loginApproval') }}</h2>
    <p class="totp-step__hint">{{ t('auth.enterTotpCode') }}</p>

    <form class="totp-step__form" @submit.prevent="submit">
      <input
        ref="inputRef"
        :value="code"
        class="totp-step__input ym-disable-keys"
        data-test="totp-input"
        type="text"
        inputmode="numeric"
        autocomplete="one-time-code"
        maxlength="6"
        :disabled="isLoading"
        @input="onInput"
      />

      <p v-if="error" class="totp-step__error" data-test="totp-error">{{ error }}</p>

      <button type="submit" class="totp-step__btn" :disabled="isLoading || !isComplete">
        {{ isLoading ? t('auth.verifying') : t('auth.confirm') }}
      </button>
    </form>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { ProfileApi } from '@/modules/profile/api/profileApi'
import { AuthRequestCancelled } from '@/shared/api/httpClient'
import { parseApiError } from '@/shared/utils/apiErrorParser'

/**
 * OTP-R6c — второй шаг входа для аккаунта с приложением-аутентификатором.
 *
 * Первый фактор (код из письма, magic) уже дал pending-ключ. Здесь человек
 * вводит 6 цифр из приложения; ключ уходит только на /profile/login-approval.
 * Входом ключ становится лишь после ответа сервера — тогда шаг сообщает
 * `approved`, а садит сессию родитель. Ничего не доставляется, поэтому
 * «отправить ещё раз» и выбора канала здесь нет.
 *
 * Ответ отменённой попытки (unmount, другой ключ) молча отбрасывается.
 */
const props = defineProps<{
  pendingToken: string
  /**
   * Как отправить код. По умолчанию — ProfileApi (кабинет, httpClient).
   * Публичные страницы key.igra.click не ходят через httpClient (там нет
   * хранилища входа и 401-логаута) и передают свой запрос; ошибки в той же
   * форме: `response.status` и `response.data.error_code`.
   */
  confirm?: (pendingToken: string, code: string, isCurrent: () => boolean) => Promise<void>
}>()

const emit = defineEmits<{
  (e: 'approved'): void
  /** Pending-ключ сервер больше не знает (истёк, отозван): начать вход заново. */
  (e: 'expired'): void
}>()

const { t } = useI18n()

// Неверный код: checkConfirmAction отдаёт его через showSwalError, и код
// ошибки теряется — на проводе HTTP 406 и error_code 0. 26 — тот же
// GUARD_CONFIRM_ACTION_CODE_BAD, если сервер когда-нибудь отдаст его как есть.
const WRONG_CODES = [0, 26]
// R6c: сервер на время перестал принимать коды из приложения (лимит неверных).
// Pending-ключ жив: после паузы новый код примется.
const CODE_LOCKED = 31

const code = ref('')
const error = ref('')
const isLoading = ref(false)
const inputRef = ref<HTMLInputElement | null>(null)
const isComplete = computed(() => /^\d{6}$/.test(code.value))

let generation = 0
onBeforeUnmount(() => { generation++ })
watch(() => props.pendingToken, () => {
  generation++
  code.value = ''
  error.value = ''
  isLoading.value = false
})

onMounted(() => inputRef.value?.focus())

function onInput(event: Event): void {
  const input = event.target as HTMLInputElement
  code.value = input.value.replace(/\D/g, '').slice(0, 6)
  // Поле показывает ровно то, что уйдёт на сервер.
  input.value = code.value
  error.value = ''
}

async function submit(): Promise<void> {
  if (isLoading.value || !isComplete.value) return

  const current = generation
  const isCurrent = () => current === generation
  isLoading.value = true
  error.value = ''

  try {
    await (props.confirm ?? ProfileApi.confirmLoginTotp)(props.pendingToken, code.value, isCurrent)
    if (!isCurrent()) return
    // Вход подтверждён: поле остаётся занятым, второго кода не будет.
    emit('approved')
  } catch (e: unknown) {
    if (e instanceof AuthRequestCancelled || !isCurrent()) return
    isLoading.value = false
    const status = (e as { response?: { status?: number } })?.response?.status
    if (status === 401) {
      emit('expired')

      return
    }
    const parsed = parseApiError(e)
    if (parsed.code === CODE_LOCKED) error.value = t('auth.totpLogin.tooManyAttempts')
    else if (status === 406 && parsed.code !== undefined && WRONG_CODES.includes(parsed.code)) error.value = t('auth.totpLogin.wrongCode')
    else error.value = t('auth.totpLogin.failed')
    code.value = ''
    nextTick(() => inputRef.value?.focus())
  }
}
</script>

<style scoped lang="scss">
.totp-step {
  display: flex;
  flex-direction: column;
  gap: 12px;
  text-align: center;

  &__title {
    margin: 0;
    font-size: var(--p-text-28, 24px);
    font-weight: var(--ds-weight-bold, 700);
    color: var(--ds-text);
  }

  &__hint {
    margin: 0 0 8px;
    color: var(--ds-text-secondary);
  }

  &__form {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__input {
    width: 100%;
    padding: 12px 18px;
    border: 1px solid var(--ds-border);
    border-radius: var(--ds-input-radius, 12px);
    background: var(--ds-overlay-04);
    color: var(--ds-text);
    font-size: var(--ds-text-size-h-md, 22px);
    letter-spacing: 0.4em;
    text-align: center;

    &:focus {
      outline: none;
      border-color: var(--ds-accent);
      box-shadow: var(--ds-focus-ring-shadow);
    }
  }

  &__error {
    margin: 0;
    color: var(--ds-danger);
    font-size: var(--ds-text-size-body, 14px);
  }

  &__btn {
    padding: 14px;
    border: none;
    border-radius: var(--ds-radius-control);
    background: linear-gradient(180deg, var(--ds-accent-light), var(--ds-accent));
    color: var(--ds-text-on-accent);
    font-weight: var(--ds-weight-semibold, 600);
    cursor: pointer;

    &:disabled {
      opacity: 0.6;
      cursor: default;
    }
  }
}
</style>
